import { PRESETS, ELEMENTS, SCHEMA, DATA_VERSION, TOTAL_TOLERANCE, EXAMPLE, findElement, validateRecipe, normalizeRecipe, calculate, parseRecipe, encodeRecipe, decodeRecipe, resultText, resultsCsv } from './alloy-composition-core.js?v=hea-design-20261012-1';
const STORAGE_KEY = 'basis:alloy-compositions:v1';
const QUOTE_KEY = 'basis:alloy-quote:v1';
export function initializeCalculator(root) {
  if (root.dataset.ready) return;
  root.dataset.ready = 'true';
  const el = (name) => root.querySelector(`[data-${name}]`);
  const rows = el('rows');
  let currentResult = null;
  let rowCounter = 0;
  let library = [];
  let libraryReadable = true;
  const say = (message) => { el('storage-status').textContent = message; };
  const display = (number) => number.toLocaleString('en-US', { maximumFractionDigits: 6, useGrouping: false });
  const list = el('elements');
  ELEMENTS.forEach((element) => {
    const option = document.createElement('option');
    option.value = element.symbol;
    option.label = `${element.name}${element.weight === null ? ' — no standard atomic weight; unsupported' : ''}`;
    list.append(option);
  });
  function addRow(symbol = '', value = '', focus = false) {
    if (rows.children.length >= 84) return say('A maximum of 84 supported elements can be used.');
    const row = document.createElement('div');
    row.className = 'alloy-tool__row';
    row.dataset.row = '';
    const id = ++rowCounter;
    row.innerHTML = `<label>Element<input type="text" data-element list="${list.id}" autocomplete="off" maxlength="60"></label><label><span data-input-label></span><input type="number" data-value step="any" min="0" inputmode="decimal"></label><button type="button" data-remove>Remove</button>`;
    row.querySelector('[data-element]').setAttribute('aria-label', `Element ${id}`);
    row.querySelector('[data-value]').setAttribute('aria-label', `Composition ${id}`);
    row.querySelector('[data-remove]').setAttribute('aria-label', `Remove element row ${id}`);
    row.querySelector('[data-element]').value = symbol;
    row.querySelector('[data-value]').value = value;
    row.querySelector('[data-remove]').addEventListener('click', () => {
      const next = row.nextElementSibling || row.previousElementSibling;
      row.remove();
      if (!rows.children.length) addRow();
      (next?.querySelector('input') || rows.querySelector('input')).focus();
      update();
    });
    rows.append(row);
    if (focus) row.querySelector('input').focus();
  }
  function rawRecipe() {
    return { schema: SCHEMA, version: 1, atomicWeights: DATA_VERSION, name: el('name').value, basis: root.querySelector('[name="alloy-basis"]:checked').value, elements: [...rows.children].map((row) => ({ symbol: findElement(row.querySelector('[data-element]').value)?.symbol || '', input: row.querySelector('[data-value]').value })), batch: { value: el('batch').value, unit: el('unit').value } };
  }
  function readRecipe() { return validateRecipe(rawRecipe()); }
  function update() {
    const raw = rawRecipe();
    const total = raw.elements.reduce((sum, row) => sum + (Number.isFinite(Number(row.input)) ? Number(row.input) : 0), 0);
    el('total').textContent = `${display(total)}%`;
    const normalized = Math.abs(total - 100) <= TOTAL_TOLERANCE;
    el('total-help').textContent = normalized ? 'Input totals 100%. Values have not been silently changed.' : 'Input does not total 100%. Correct the values or explicitly choose Normalize to 100%.';
    el('basis-label').textContent = raw.basis === 'atomic' ? 'Input: at% · Output: wt%' : 'Input: wt% · Output: at%';
    root.querySelectorAll('[data-input-label]').forEach((node) => { node.textContent = raw.basis === 'atomic' ? 'Input at%' : 'Input wt%'; });
    root.querySelectorAll('[data-remove]').forEach((button) => { button.disabled = rows.children.length === 1; });
    currentResult = null;
    el('result-content').hidden = true;
    el('result-empty').hidden = false;
    el('share-wrap').hidden = true;
    el('copy-wrap').hidden = true;
    try {
      currentResult = calculate(raw);
      el('error').textContent = '';
      el('result-content').hidden = false;
      el('result-empty').hidden = true;
      el('result-basis').textContent = `${raw.basis === 'atomic' ? 'Atomic percent (at%) input → weight percent (wt%) output.' : 'Weight percent (wt%) input → atomic percent (at%) output.'} Mass is based on weight fraction.`;
      el('mass-heading').textContent = `Mass (${raw.batch.unit})`;
      el('mass-total').textContent = display(Number(raw.batch.value));
      el('formula').textContent = currentResult.formula;
      const d = currentResult.descriptors;
      el('descriptors').replaceChildren();
      [['Constituent elements (positive concentration)', d.constituents], ['Ideal ΔS_mix', `${display(d.entropy)} J/(mol·K)`], ['ΔS_mix / R', display(d.entropyOverR)], ['Atomic size mismatch δ', d.delta === null ? 'Unavailable' : `${display(d.delta)}%`], ['VEC', d.vec === null ? 'Unavailable' : display(d.vec)]].forEach(([label, value]) => {
        const dt = document.createElement('dt'); dt.textContent = label;
        const dd = document.createElement('dd'); dd.textContent = value;
        el('descriptors').append(dt, dd);
      });
      el('descriptor-status').textContent = d.missing.length ? `Radius and VEC data unavailable for: ${d.missing.join(', ')}. Neither descriptor is calculated with incomplete data. Entropy and conversions remain available.` : 'All positive-concentration elements have reference radius and VEC data. Values are screening descriptors, not measured properties.';
      el('results').replaceChildren();
      currentResult.rows.forEach((result) => {
        const tr = document.createElement('tr');
        const th = document.createElement('th');
        th.scope = 'row';
        th.textContent = result.symbol;
        const small = document.createElement('small');
        small.textContent = result.name;
        th.append(small);
        tr.append(th);
        [result.atomic, result.weight, result.massGrams / (raw.batch.unit === 'kg' ? 1000 : 1)].forEach((value) => { const td = document.createElement('td'); td.textContent = display(value); tr.append(td); });
        el('results').append(tr);
      });
    } catch (error) { el('error').textContent = error.message; }
    ['csv', 'copy', 'reverse', 'quote'].forEach((action) => { root.querySelector(`[data-action="${action}"]`).disabled = !currentResult; });
  }
  function load(input) {
    const recipe = validateRecipe(input);
    rows.replaceChildren();
    el('name').value = recipe.name;
    root.querySelector(`[name="alloy-basis"][value="${recipe.basis}"]`).checked = true;
    el('batch').value = recipe.batch.value;
    el('unit').value = recipe.batch.unit;
    recipe.elements.forEach((row) => addRow(row.symbol, row.input));
    update();
  }
  function refreshLibrary(selected = '') {
    const select = el('saved');
    select.replaceChildren(new Option('Select a saved composition', ''));
    library.forEach((item) => select.append(new Option(item.recipe.name, item.id)));
    select.value = selected;
  }
  function persist(next) {
    if (!libraryReadable) throw new Error('Saved data could not be read safely. Export your current recipe; existing saved data has not been overwritten.');
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); }
    catch { throw new Error('Browser storage is unavailable or full. Export JSON to keep this composition.'); }
    library = next;
  }
  function selected() {
    const item = library.find((entry) => entry.id === el('saved').value);
    if (!item) throw new Error('Select a saved composition first.');
    return item;
  }
  function download(content, filename, type) {
    const url = URL.createObjectURL(new Blob([content], { type }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  function requireResult() {
    if (!currentResult) throw new Error('Correct the composition and batch mass before using the results.');
    return currentResult;
  }
  const actions = {
    add: () => { addRow('', '', true); update(); },
    example: () => { load(structuredClone(EXAMPLE)); say('Loaded Co, Cr, Fe, Mn and Ni at 20 at% each — not 20 wt% each.'); },
    normalize: () => { load(normalizeRecipe(readRecipe())); say('Explicitly normalized the input values to 100%.'); },
    reverse: () => {
      const result = requireResult();
      const basis = result.recipe.basis === 'atomic' ? 'weight' : 'atomic';
      load({ ...result.recipe, basis, elements: result.rows.map((row) => ({ symbol: row.symbol, input: String(basis === 'weight' ? row.weight : row.atomic) })) });
      say('Loaded full-precision converted values as the new input.');
    },
    save: () => {
      const recipe = readRecipe();
      if (!recipe.name) throw new Error('Enter a name before saving.');
      if (library.length >= 50) throw new Error('This browser already has 50 saved compositions. Export or delete an older one first.');
      const item = { id: crypto.randomUUID(), recipe };
      persist([...library, item]);
      refreshLibrary(item.id);
      say(`Saved “${recipe.name}” in this browser.`);
    },
    load: () => { const item = selected(); load(item.recipe); say(`Loaded “${item.recipe.name}”.`); },
    rename: () => {
      const item = selected();
      const name = el('name').value.trim();
      if (!name) throw new Error('Enter the new name in Composition name.');
      const recipe = validateRecipe({ ...item.recipe, name });
      persist(library.map((entry) => entry.id === item.id ? { ...entry, recipe } : entry));
      refreshLibrary(item.id);
      say(`Renamed the saved composition to “${name}”.`);
    },
    delete: () => { const item = selected(); persist(library.filter((entry) => entry.id !== item.id)); refreshLibrary(); say(`Deleted “${item.recipe.name}” from this browser.`); },
    json: () => { download(JSON.stringify(readRecipe(), null, 2), 'alloy-composition.json', 'application/json'); say('Exported composition JSON.'); },
    csv: () => { download(resultsCsv(requireResult()), 'alloy-results.csv', 'text/csv;charset=utf-8'); say('Exported full-precision results CSV; masses are in grams.'); },
    copy: async () => {
      const text = resultText(requireResult());
      el('copy').value = text;
      el('copy-wrap').hidden = false;
      try { await navigator.clipboard.writeText(text); say('Copied plain-text results.'); }
      catch { el('copy').focus(); el('copy').select(); say('Clipboard permission unavailable. Copy the selected plain text manually.'); }
    },
    share: () => {
      const url = new URL(location.href);
      [...url.searchParams.keys()].forEach((key) => {
        if (!['preview_theme_id', 'preview_key', 'view'].includes(key)) url.searchParams.delete(key);
      });
      if (root.dataset.previewThemeId) url.searchParams.set('preview_theme_id', root.dataset.previewThemeId);
      url.hash = 'composition=' + encodeRecipe(readRecipe());
      if (url.href.length > 16000) throw new Error('This composition is too large for a share link. Export JSON instead.');
      el('share').value = url.href;
      el('share-wrap').hidden = false;
      say('Share link generated. It contains your composition; do not share confidential recipes.');
    },
    quote: () => {
      const result = requireResult();
      const payload = { version: 1, recipe: result.recipe, form: el('form').value, quantity: el('quantity').value.trim(), dimensions: el('dimensions').value.trim(), notes: [el('purity').value.trim() && `Required purity: ${el('purity').value.trim()}`, el('application').value.trim() && `Application: ${el('application').value.trim()}`, el('notes').value.trim()].filter(Boolean).join('\n') };
      if (payload.notes.length > 2000) throw new Error('Combined purity, application and notes must be no more than 2000 characters.');
      try { sessionStorage.setItem(QUOTE_KEY, JSON.stringify(payload)); }
      catch { throw new Error('Session storage is unavailable. Copy the plain-text result and paste it into the quote form.'); }
      const quoteUrl = new URL(root.dataset.quoteUrl, location.origin);
      if (root.dataset.previewThemeId) quoteUrl.searchParams.set('preview_theme_id', root.dataset.previewThemeId);
      location.assign(quoteUrl.href);
    }
  };
  root.querySelectorAll('[data-action]').forEach((button) => button.addEventListener('click', async () => {
    try { await actions[button.dataset.action](); }
    catch (error) { say(error.message); }
  }));
  document.querySelectorAll('[data-preset]').forEach((button) => button.addEventListener('click', () => {
    if (!PRESETS[button.dataset.preset]) return;
    load(structuredClone(PRESETS[button.dataset.preset]));
    say(`Loaded nominal equiatomic ${button.dataset.preset}. Availability requires review.`);
    if (!root.contains(button)) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }));
  rows.addEventListener('input', update);
  root.querySelectorAll('[name="alloy-basis"], [data-batch], [data-unit]').forEach((input) => input.addEventListener('input', update));
  el('name').addEventListener('input', update);
  el('import').addEventListener('change', async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      if (file.size > 65536) throw new Error('Composition files must be smaller than 64 KB.');
      const recipe = parseRecipe(await file.text());
      load(recipe);
      say('Imported composition. Save it to keep it in this browser.');
    } catch (error) { say(error.message); }
    event.target.value = '';
  });
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed) || parsed.length > 50 || new Set(parsed.map((item) => item.id)).size !== parsed.length) throw new Error('Invalid saved library.');
      library = parsed.map((item) => {
        if (typeof item.id !== 'string' || item.id.length > 100) throw new Error('Invalid saved composition ID.');
        return { id: item.id, recipe: validateRecipe(item.recipe) };
      });
    }
  } catch { libraryReadable = false; say('Saved compositions could not be read. Existing browser data is untouched; JSON import/export remains available.'); }
  refreshLibrary();
  addRow();
  update();
  const preset = new URLSearchParams(location.search).get('preset');
  if (Object.hasOwn(PRESETS, preset)) load(structuredClone(PRESETS[preset]));
  const share = new URLSearchParams(location.hash.slice(1)).get('composition');
  if (share) {
    history.replaceState(null, '', location.pathname + location.search);
    try { load(decodeRecipe(share)); say('Loaded a composition from this share link.'); }
    catch (error) { say(error.message); }
  }
  el('workspace').hidden = false;
}
document.querySelectorAll('[data-alloy-calculator]').forEach(initializeCalculator);
document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-alloy-calculator]').forEach(initializeCalculator));
